import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  Edit, 
  CreditCard, 
  Users, 
  Package, 
  CreditCard as CreditCardIcon, 
  BarChart, 
  Download, 
  ChevronUp, 
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  Building,
  FileText,
  ExternalLink,
  MoreHorizontal,
  Loader2,
  AlertCircle,
  Activity,
  DollarSign,
  Calendar,
  TrendingUp,
  Star,
  LayoutGrid
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { formatDate, formatCurrency, cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import CustomerContactsTable from '@/components/customers/CustomerContactsTable';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ImportHubspotContactDialog from '@/components/customers/ImportHubspotContactDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/common/StatusBadge';
import CustomerTenantsTab from '@/components/customers/CustomerTenantsTab';
import { Separator } from '@/components/ui/separator';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts';

interface SubscriptionItem {
  subscription_plan: {
    name: string;
    billing_period_type?: string;
    prices?: {
      currency: string;
      price_amount: number;
    }[];
  };
  quantity: number;
  unit_price: number;
}

interface SubscriptionResponse {
  id: string;
  state: string;
  start_date: string;
  end_date: string | null;
  next_billing_at: string | null;
  items: SubscriptionItem[];
}

interface Subscription {
  id: string;
  state: string;
  start_date: string;
  end_date: string | null;
  next_billing_at: string | null;
  subscription_plan: {
    name: string;
    prices?: {
      currency: string;
      price_amount: number;
    }[];
    billing_period_type?: string;
  };
  quantity: number;
  unit_price: number;
}

interface Customer {
  id: string;
  name: string;
  business_identification_number?: string;
  legal_name?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  domain?: string;
  logo_url?: string;
  created_at?: string;
  updated_at?: string;
  description?: string;
  status?: string;
  email?: string;
  hubspot_owner_id?: string;
  responsible_user_name?: string;
  industry?: string;
}

interface MAUData {
  tenant_id: string;
  customer_id: string;
  year_month: string;
  month_start: string;
  maus: number;
  prev_maus: number | null;
  mom_abs_change: number | null;
  mom_pct_change: string | null;
}

const CustomerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [contactsCount, setContactsCount] = useState<number>(0);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [failedImage, setFailedImage] = useState(false);
  const [isImportContactDialogOpen, setIsImportContactDialogOpen] = useState(false);
  const [contactsRefreshTrigger, setContactsRefreshTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [mauData, setMauData] = useState<MAUData[]>([]);
  const [mauLoading, setMauLoading] = useState(false);

  // Função para obter as iniciais do nome do cliente
  const getInitials = (name: string) => {
    if (!name) return 'C';
    return name.charAt(0).toUpperCase();
  };

  // Função para lidar com erro de carregamento da imagem
  const handleImageError = () => {
    setFailedImage(true);
  };

  useEffect(() => {
    if (id) {
      fetchCustomer(id);
      fetchCustomerSubscriptions(id);
      fetchContactsCount(id);
      fetchMAUData(id);
    }
  }, [id]);

  const fetchCustomer = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();

      if (error) throw error;
      setCustomer(data as Customer);
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      toast.error('Não foi possível carregar os dados do cliente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchContactsCount = async (customerId: string) => {
    try {
      setContactsLoading(true);
      
      // Directly count contacts for the customer
      const { count, error: contactsError } = await supabase
        .from('customer_contacts')
        .select('*', { count: 'exact', head: true })
        .eq('customer_id', customerId)
        .eq('is_active', true);

      if (contactsError) {
        console.error('Erro ao contar contatos:', contactsError);
        throw contactsError;
      }
      
      setContactsCount(count || 0);
    } catch (error) {
      console.error('Erro ao buscar contagem de contatos:', error);
      toast.error('Não foi possível carregar a contagem de contatos.');
    } finally {
      setContactsLoading(false);
    }
  };

  const fetchMAUData = async (customerId: string) => {
    try {
      setMauLoading(true);
      
      // Fetch MAU data directly using customer_id column in the view
      const { data: mauDataResult, error: mauError } = await supabase
        .from('analytics_mau_by_tenant')
        .select('*')
        .eq('customer_id', customerId)
        .order('year_month', { ascending: true });

      if (mauError) {
        console.error('Erro ao buscar dados de MAU:', mauError);
        throw mauError;
      }

      setMauData(mauDataResult || []);
    } catch (error) {
      console.error('Erro ao buscar dados de MAU:', error);
      toast.error('Não foi possível carregar os dados de MAU.');
      setMauData([]);
    } finally {
      setMauLoading(false);
    }
  };

  const fetchCustomerSubscriptions = async (customerId: string) => {
    try {
      setSubscriptionsLoading(true);
      
      // Primeiro, buscar os relacionamentos do cliente
      const { data: relationshipsData, error: relationshipsError } = await supabase
        .from('customer_relationship')
        .select('id')
        .eq('customer_id', customerId);
        
      if (relationshipsError) {
        console.error('Erro ao buscar relacionamentos:', relationshipsError);
        throw relationshipsError;
      }
      
      if (!relationshipsData || relationshipsData.length === 0) {
        setSubscriptions([]);
        setSubscriptionsLoading(false);
        return;
      }
      
      // Extrair os IDs dos relacionamentos
      const relationshipIds = relationshipsData.map(rel => rel.id);
      
      // Buscar as assinaturas para esses relacionamentos
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('subscriptions')
        .select('id, state, start_date, end_date, next_billing_at')
        .in('customer_relationship_id', relationshipIds)
        .order('start_date', { ascending: false });

      if (subscriptionsError) {
        console.error('Erro ao buscar assinaturas:', subscriptionsError);
        throw subscriptionsError;
      }
      
      if (!subscriptionsData || subscriptionsData.length === 0) {
        setSubscriptions([]);
        setSubscriptionsLoading(false);
        return;
      }
      
      // Array para armazenar as assinaturas processadas
      const processedSubscriptions: Subscription[] = [];
      
      // Para cada assinatura, buscar os itens da assinatura
      for (const subscription of subscriptionsData) {
        try {
          const { data: itemData, error: itemError } = await supabase
            .from('subscription_items')
            .select(`
              id,
              quantity, 
              unit_price,
              subscription_plan_id
            `)
            .eq('subscription_id', subscription.id)
            .limit(1)
            .maybeSingle();

          if (itemError && itemError.code !== 'PGRST116') {
            console.error('Erro ao buscar item da assinatura:', itemError);
            // Adicionar assinatura com valores padrão
            processedSubscriptions.push({
              ...subscription,
              subscription_plan: { 
                name: 'Plano não encontrado', 
                prices: [{ currency: 'BRL', price_amount: 0 }],
                billing_period_type: 'monthly'
              },
              quantity: 1,
              unit_price: 0
            });
            continue;
          }
          
          if (!itemData) {
            // Adicionar assinatura com valores padrão quando não há items
            processedSubscriptions.push({
              ...subscription,
              subscription_plan: { 
                name: 'Plano não encontrado', 
                prices: [{ currency: 'BRL', price_amount: 0 }],
                billing_period_type: 'monthly' 
              },
              quantity: 1,
              unit_price: 0
            });
            continue;
          }
          
          // Buscar os detalhes do plano
          const { data: planData, error: planError } = await supabase
            .from('subscription_plans')
            .select(`
              name, 
              billing_period_type,
              prices:subscription_plan_prices (
                currency,
                price_amount
              )
            `)
            .eq('id', itemData.subscription_plan_id)
            .single();
            
          if (planError) {
            console.error('Erro ao buscar plano:', planError);
            // Adicionar assinatura com valores padrão para o plano
            processedSubscriptions.push({
              ...subscription,
              subscription_plan: { 
                name: 'Plano não encontrado', 
                prices: [{ currency: 'BRL', price_amount: 0 }],
                billing_period_type: 'monthly'
              },
              quantity: itemData.quantity || 1,
              unit_price: itemData.unit_price || 0
            });
            continue;
          }
          
          // Garantir que prices sempre seja um array válido
          const validPrices = Array.isArray(planData?.prices) && planData.prices.length > 0 
            ? planData.prices 
            : [{ currency: 'BRL', price_amount: 0 }];
          
          // Adicionar assinatura com todos os dados
          processedSubscriptions.push({
            ...subscription,
            subscription_plan: {
              name: planData?.name || 'Sem nome',
              prices: validPrices,
              billing_period_type: planData?.billing_period_type || 'monthly'
            },
            quantity: itemData.quantity || 1,
            unit_price: itemData.unit_price || 0
          });
        } catch (error) {
          console.error('Erro ao processar assinatura:', error);
          // Adicionar assinatura com valores padrão
          processedSubscriptions.push({
            ...subscription,
            subscription_plan: { 
              name: 'Erro ao carregar plano', 
              prices: [{ currency: 'BRL', price_amount: 0 }],
              billing_period_type: 'monthly'
            },
            quantity: 1,
            unit_price: 0
          });
        }
      }
      
      setSubscriptions(processedSubscriptions);
    } catch (error) {
      console.error('Erro ao buscar assinaturas:', error);
      toast.error('Não foi possível carregar as assinaturas do cliente.');
      // Em caso de erro, definir um array vazio
      setSubscriptions([]);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  // Format CNPJ
  const formatDocument = (doc: string | null) => {
    if (!doc) return '-';
    
    // Remove non-numeric characters
    const numbers = doc.replace(/\D/g, '');
    
    // Format as CNPJ (xx.xxx.xxx/xxxx-xx)
    if (numbers.length === 14) {
      return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    // Format as CPF (xxx.xxx.xxx-xx)
    else if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    
    return doc;
  };

  // Calcular métricas
  const getActiveSubscriptionsCount = () => {
    return subscriptions.filter(sub => sub.state === 'active').length;
  };

  const getActivePlansCount = () => {
    // Conta planos únicos ativos
    const uniquePlans = new Set();
    subscriptions
      .filter(sub => sub.state === 'active')
      .forEach(sub => {
        uniquePlans.add(sub.subscription_plan?.name || 'Plano Desconhecido');
      });
    return uniquePlans.size;
  };

  const getRecurringRevenue = () => {
    return subscriptions
      .filter(sub => sub.state === 'active')
      .reduce((total, sub) => {
        return total + (sub.unit_price * sub.quantity);
      }, 0);
  };

  const getBillingFrequency = (periodType: string) => {
    const periodTypeMap: Record<string, string> = {
      monthly: 'Mensal',
      yearly: 'Anual',
      quarterly: 'Trimestral',
      semiannually: 'Semestral',
      weekly: 'Semanal',
      daily: 'Diário',
      once: 'Único'
    };

    return periodTypeMap[periodType] || periodType;
  };

  // New function to handle contact imported
  const handleContactImported = () => {
    setContactsRefreshTrigger(prev => prev + 1);
    fetchContactsCount(id as string);
  };

  // Função para traduzir o campo industry para PT-BR
  const translateIndustry = (industry?: string): string => {
    if (!industry) return '';
    
    const industryMap: Record<string, string> = {
      'agriculture': 'Agricultura',
      'finance': 'Finanças',
      'technology': 'Tecnologia',
      'healthcare': 'Saúde',
      'education': 'Educação',
      'retail': 'Varejo',
      'manufacturing': 'Manufatura',
      'construction': 'Construção',
      'entertainment': 'Entretenimento',
      'transport': 'Transporte',
      'energy': 'Energia',
      'real_estate': 'Imobiliário',
      'consulting': 'Consultoria',
      'legal': 'Jurídico'
    };
    
    return industryMap[industry.toLowerCase()] || industry;
  };

  // Traduzir o status das assinaturas para PT-BR
  const getSubscriptionStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      active: 'Ativa',
      inactive: 'Inativa',
      pending: 'Pendente',
      suspended: 'Suspensa',
      cancelled: 'Cancelada',
      trial: 'Teste',
      expired: 'Expirada'
    };

    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-gradient-to-br from-[#3b65f5] via-[#ff4a8f] to-[#c8e600] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <Loader2 className="h-16 w-16 animate-spin mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Carregando Cliente</h1>
              <p className="text-white/80">Aguarde enquanto buscamos os detalhes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-gradient-to-br from-red-500 via-red-600 to-red-700 overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <AlertCircle className="h-16 w-16 mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Cliente Não Encontrado</h1>
              <p className="text-white/80 mb-6">O cliente não foi encontrado em nossos registros.</p>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/customers')}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar para Clientes
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Hero Section with Fixed Background Color */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
        
        <div className="relative z-10 container mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-white">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate('/customers')}
                  className="text-white hover:bg-white/20 border-white/30"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="h-8 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <Building className="h-6 w-6" />
                  <span className="text-white/80">Cliente</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4 mb-3">
                {customer?.domain ? (
                  <Avatar className="h-16 w-16 border-2 border-white/30">
                    <AvatarImage 
                      src={`https://cdn.brandfetch.io/${customer.domain}?c=1idk4AUs2-buvooOUvQ`} 
                      alt={customer.name} 
                      onError={() => setFailedImage(true)}
                    />
                    <AvatarFallback className="text-2xl bg-white/20 text-white">
                      {getInitials(customer.name)}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <Avatar className="h-16 w-16 border-2 border-white/30">
                    <AvatarFallback className="text-2xl bg-white/20 text-white">
                      {getInitials(customer.name)}
                    </AvatarFallback>
                  </Avatar>
                )}
                
                <div>
                  <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
                    {customer?.name || 'Cliente'}
                  </h1>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-white/90">
                {customer?.business_identification_number && (
                  <>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span>{formatDocument(customer.business_identification_number)}</span>
                    </div>
                    <div className="h-4 w-px bg-white/30" />
                  </>
                )}
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-bold">{formatCurrency(getRecurringRevenue(), 'BRL')}</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <Badge 
                  className="bg-white/20 text-white border-white/30"
                  variant={customer?.status === 'active' ? 'default' : 'secondary'}
                >
                  {customer?.status === 'active' ? 'Ativo' : customer?.status || 'N/A'}
                </Badge>
              </div>
            </div>
            
            <div className="flex gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  >
                    <MoreHorizontal className="h-4 w-4 mr-2" />
                    Ações
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem>Sincronizar com CRM</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsImportContactDialogOpen(true)}>
                    <Download className="h-4 w-4 mr-2" />
                    Importar Contatos
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                onClick={() => navigate('/customers')}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Todos
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10 space-y-8">
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Active Subscriptions Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <CreditCard className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Assinaturas Ativas</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {subscriptionsLoading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    getActiveSubscriptionsCount()
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Total: {subscriptions.length}
              </div>
            </CardContent>
          </Card>

          {/* Active Plans Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <Package className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Planos Ativos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {subscriptionsLoading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    getActivePlansCount()
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Desde: {customer?.created_at ? formatDate(customer.created_at) : 'N/A'}
              </div>
            </CardContent>
          </Card>

          {/* Recurring Revenue Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-orange-100 rounded-md">
                    <DollarSign className="h-4 w-4 text-orange-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Receita Recorrente</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {subscriptionsLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    formatCurrency(getRecurringRevenue(), 'BRL')
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Mensal
              </div>
            </CardContent>
          </Card>

          {/* Contacts Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    <Users className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Contatos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {contactsLoading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    contactsCount
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {contactsCount > 0 ? 'Ativos' : 'Nenhum'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Main Content */}
        <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
          <CardContent className="p-6">
            {/* Tabs Navigation */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-6 max-w-fit">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                  activeTab === 'overview'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
                Visão Geral
              </button>

              <button
                onClick={() => setActiveTab('subscriptions')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                  activeTab === 'subscriptions'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <CreditCard className="h-4 w-4" />
                Assinaturas
              </button>

              <button
                onClick={() => setActiveTab('contacts')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                  activeTab === 'contacts'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Users className="h-4 w-4" />
                Contatos
              </button>

              <button
                onClick={() => setActiveTab('tenants')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                  activeTab === 'tenants'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Building className="h-4 w-4" />
                Tenants
              </button>

              <button
                onClick={() => setActiveTab('analytics')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                  activeTab === 'analytics'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <BarChart className="h-4 w-4" />
                Analytics
              </button>
            </div>
          
          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Customer Information */}
              <Card className="lg:col-span-1 border shadow-sm bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building className="h-5 w-5 text-blue-600" />
                    Informações do Cliente
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {customer.legal_name && (
                    <div>
                      <h3 className="text-sm font-extrabold text-gray-600 mb-2">Razão Social</h3>
                      <div className="font-extrabold text-gray-900">{customer.legal_name}</div>
                    </div>
                  )}
                  
                  {customer.business_identification_number && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">CNPJ</h3>
                        <div className="font-extrabold text-gray-900">{formatDocument(customer.business_identification_number)}</div>
                      </div>
                    </>
                  )}
                  
                  {customer.email && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">Email</h3>
                        <div className="font-extrabold text-gray-900">{customer.email}</div>
                      </div>
                    </>
                  )}
                  
                  {customer.phone && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">Telefone</h3>
                        <div className="font-extrabold text-gray-900">{customer.phone}</div>
                      </div>
                    </>
                  )}
                  
                  {(customer.address || customer.city || customer.state) && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">Endereço</h3>
                        <div className="font-extrabold text-gray-900">
                          {customer.address}
                          {customer.address2 ? `, ${customer.address2}` : ''}
                          {customer.city ? `, ${customer.city}` : ''}
                          {customer.state ? ` - ${customer.state}` : ''}
                          {customer.zip ? ` CEP: ${customer.zip}` : ''}
                        </div>
                      </div>
                    </>
                  )}

                  {customer.industry && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">Setor</h3>
                        <div className="font-extrabold text-gray-900">{translateIndustry(customer.industry)}</div>
                      </div>
                    </>
                  )}

                  <Separator />
                  <div>
                    <h3 className="text-sm font-extrabold text-gray-600 mb-2">Cronologia</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Criado</span>
                        <span className="text-sm font-extrabold">{customer.created_at ? formatDate(customer.created_at) : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Atualizado</span>
                        <span className="text-sm font-extrabold">{customer.updated_at ? formatDate(customer.updated_at) : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Subscriptions */}
              <Card className="lg:col-span-2 border shadow-sm bg-gradient-to-br from-white to-gray-50">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-green-600" />
                    Assinaturas Recentes
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={() => setActiveTab('subscriptions')}>
                    Ver todas
                  </Button>
                </CardHeader>
                <CardContent>
                  {subscriptionsLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-12 w-full" />
                      <Skeleton className="h-12 w-full" />
                    </div>
                  ) : subscriptions.length === 0 ? (
                    <div className="bg-muted/20 rounded-lg p-6 text-center">
                      <p className="text-muted-foreground">Nenhuma assinatura encontrada</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {subscriptions.slice(0, 3).map((subscription) => (
                        <div 
                          key={subscription.id}
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => navigate(`/subscriptions/${subscription.id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-2 h-10 rounded-full",
                              subscription.state === 'active' ? "bg-green-500" : "bg-gray-300"
                            )}/>
                            <div>
                              <p className="font-extrabold">{subscription.subscription_plan?.name || "Plano não encontrado"}</p>
                              <div className="flex items-center text-xs text-muted-foreground">
                                <span>{getBillingFrequency(subscription.subscription_plan?.billing_period_type || 'monthly')}</span>
                                <span className="mx-1">•</span>
                                <span>Início: {formatDate(subscription.start_date)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-extrabold">
                              {formatCurrency(
                                (subscription.unit_price || 0) * (subscription.quantity || 0), 
                                'BRL'
                              )}
                            </p>
                            <Badge variant={subscription.state === 'active' ? 'default' : 'secondary'} className="text-xs">
                              {getSubscriptionStatusLabel(subscription.state)}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Subscriptions Tab Content */}
          {activeTab === 'subscriptions' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Assinaturas</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/subscription-wizard/step1')}
                >
                  Nova Assinatura
                </Button>
              </CardHeader>
              <CardContent>
                {subscriptionsLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : subscriptions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Este cliente não possui assinaturas.
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Plano</TableHead>
                          <TableHead>Quantidade</TableHead>
                          <TableHead>Valor Unit.</TableHead>
                          <TableHead>Cobrança</TableHead>
                          <TableHead>Valor Total</TableHead>
                          <TableHead>Data de Início</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subscriptions.map((subscription) => (
                          <TableRow key={subscription.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/subscriptions/${subscription.id}`)}>
                            <TableCell className="font-extrabold">{subscription.subscription_plan?.name || '-'}</TableCell>
                            <TableCell>{subscription.quantity || 0}</TableCell>
                            <TableCell>
                              {formatCurrency(
                                subscription.unit_price || 0, 
                                (subscription.subscription_plan?.prices && 
                                Array.isArray(subscription.subscription_plan.prices) && 
                                subscription.subscription_plan.prices.length > 0) 
                                  ? subscription.subscription_plan.prices[0].currency 
                                  : 'BRL'
                              )}
                            </TableCell>
                            <TableCell>{getBillingFrequency(subscription.subscription_plan?.billing_period_type || 'monthly')}</TableCell>
                            <TableCell>
                              {formatCurrency(
                                (subscription.unit_price || 0) * (subscription.quantity || 0), 
                                (subscription.subscription_plan?.prices && 
                                Array.isArray(subscription.subscription_plan.prices) && 
                                subscription.subscription_plan.prices.length > 0) 
                                  ? subscription.subscription_plan.prices[0].currency 
                                  : 'BRL'
                              )}
                            </TableCell>
                            <TableCell>{formatDate(subscription.start_date)}</TableCell>
                            <TableCell>
                              <StatusBadge status={subscription.state} context="subscription" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Contacts Tab Content */}
          {activeTab === 'contacts' && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Contatos</CardTitle>
                <Button 
                  variant="outline" 
                  onClick={() => setIsImportContactDialogOpen(true)}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Importar Contatos
                </Button>
              </CardHeader>
              <CardContent>
                <CustomerContactsTable
                  customerId={customer?.id ?? ''}
                  refreshTrigger={contactsRefreshTrigger}
                />
              </CardContent>
            </Card>
          )}

          {/* Tenants Tab Content */}
          {activeTab === 'tenants' && (
            <CustomerTenantsTab customerId={customer?.id ?? ''} />
          )}

          {/* Analytics Tab Content */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* MAU Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart className="h-5 w-5 text-blue-600" />
                    Usuários Ativos Mensais (MAU)
                  </CardTitle>
                  <CardDescription>
                    Evolução dos usuários ativos mensais ao longo do tempo
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {mauLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : mauData.length === 0 ? (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      <div className="text-center">
                        <BarChart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Nenhum dado de MAU disponível</p>
                      </div>
                    </div>
                  ) : (
                    <ChartContainer
                      config={{
                        maus: {
                          label: "MAUs",
                          color: "hsl(var(--chart-1))",
                        },
                      }}
                      className="h-64"
                    >
                      <LineChart data={mauData}>
                        <XAxis 
                          dataKey="year_month"
                          axisLine={true}
                          tickLine={true}
                          tickFormatter={(value) => {
                            const [year, month] = value.split('-');
                            return `${month}/${year}`;
                          }}
                        />
                        <YAxis 
                          axisLine={true}
                          tickLine={true}
                        />
                        <ChartTooltip 
                          content={<ChartTooltipContent />}
                          labelFormatter={(value) => {
                            const [year, month] = value.split('-');
                            return `${month}/${year}`;
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="maus" 
                          stroke="var(--color-maus)" 
                          strokeWidth={2}
                          dot={{ fill: "var(--color-maus)" }}
                        />
                      </LineChart>
                    </ChartContainer>
                  )}
                </CardContent>
              </Card>

              {/* MAU Data Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Dados Detalhados de MAU</CardTitle>
                  <CardDescription>
                    Tabela com dados mensais e variações
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {mauLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ) : mauData.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Nenhum dado de MAU disponível para este cliente.
                    </div>
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Mês/Ano</TableHead>
                            <TableHead>MAUs</TableHead>
                            <TableHead>MAUs Anterior</TableHead>
                            <TableHead>Variação Absoluta</TableHead>
                            <TableHead>Variação (%)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mauData.map((item) => (
                            <TableRow key={`${item.tenant_id}-${item.year_month}`}>
                              <TableCell className="font-extrabold">
                                {(() => {
                                  const [year, month] = item.year_month.split('-');
                                  return `${month}/${year}`;
                                })()}
                              </TableCell>
                              <TableCell className="font-extrabold text-blue-600">
                                {item.maus.toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {item.prev_maus ? item.prev_maus.toLocaleString() : '-'}
                              </TableCell>
                              <TableCell>
                                {item.mom_abs_change !== null ? (
                                  <span className={cn(
                                    "font-extrabold",
                                    item.mom_abs_change > 0 ? "text-green-600" : 
                                    item.mom_abs_change < 0 ? "text-red-600" : "text-gray-600"
                                  )}>
                                    {item.mom_abs_change > 0 ? '+' : ''}{item.mom_abs_change.toLocaleString()}
                                  </span>
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                {item.mom_pct_change !== null ? (
                                  <Badge 
                                    variant={
                                      parseFloat(item.mom_pct_change) > 0 ? 'default' : 
                                      parseFloat(item.mom_pct_change) < 0 ? 'destructive' : 'secondary'
                                    }
                                    className="font-extrabold"
                                  >
                                    {parseFloat(item.mom_pct_change) > 0 ? '+' : ''}{item.mom_pct_change}%
                                  </Badge>
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Spacing */}
      <div className="h-16" />
      
      {/* Import Contact Dialog */}
      <ImportHubspotContactDialog
        customerId={id as string}
        isOpen={isImportContactDialogOpen}
        onClose={() => setIsImportContactDialogOpen(false)}
        onContactImported={handleContactImported}
      />
    </div>
  );
};

export default CustomerDetail; 